import { DEFAULT_TOOLTIP_GLOBAL_CLOSE_EVENTS } from 'assets/data';
import InfoCircle from 'assets/icons/InfoCircle';
import classNames from 'classnames';
import { ATTRIB } from 'constants/test';
import { ReactNode, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { PlacesType, Tooltip as ReactTooltip } from 'react-tooltip';
import { v4 as uuid } from 'uuid';

interface TooltipProps {
  message?: string;
  place?: PlacesType;
  icon?: JSX.Element | string;
  iconStyles?: string;
  iconFillDarkMode?: string;
  additionalWrapperStyles?: string;
  isCustomMessage?: boolean;
  popupStyles?: string;
  element?: JSX.Element;
  /** When set, replaces the default info-icon trigger (e.g. clamped text). */
  trigger?: ReactNode;
  triggerClassName?: string;
}

const getTooltipPortalTarget = () =>
  typeof document !== 'undefined' ? document.body : null;

const Tooltip = ({
  message,
  place,
  icon,
  iconStyles,
  iconFillDarkMode,
  additionalWrapperStyles,
  isCustomMessage = false,
  popupStyles,
  element,
  trigger,
  triggerClassName
}: TooltipProps) => {
  const id = useMemo(() => uuid(), []);
  const portalTarget = getTooltipPortalTarget();
  const hasTriggerContent = Boolean(message || element);

  const tooltipPopup = hasTriggerContent ? (
    <ReactTooltip
      id={id}
      place={place}
      positionStrategy='fixed'
      globalCloseEvents={DEFAULT_TOOLTIP_GLOBAL_CLOSE_EVENTS}
      className={classNames(
        'max-w-[80vw] md:max-w-[50vw] z-[99999] !rounded-md !opacity-100 !text-xs',
        popupStyles
          ? popupStyles
          : '!bg-primary-700 dark:!bg-primary-dark-200'
      )}>
      {isCustomMessage ? (
        <span>
          {message?.split('\n').map((line, index) => (
            <span key={index}>
              {line}
              <br />
            </span>
          ))}
        </span>
      ) : (
        message || element
      )}
    </ReactTooltip>
  ) : null;

  return (
    <span
      className={`${additionalWrapperStyles ?? 'inline-flex align-middle items-center ~ml-1/1.5'}`}>
      {trigger ? (
        <span
          data-tooltip-id={hasTriggerContent ? id : undefined}
          className={triggerClassName ?? 'cursor-help'}>
          {trigger}
        </span>
      ) : (
        <span
          data-tooltip-id={hasTriggerContent ? id : undefined}
          className='cursor-pointer'
          data-testid={ATTRIB.DATA_TESTID.TOOLTIP_ICON}>
          {icon ?? (
            <InfoCircle styles={iconStyles} fillDarkMode={iconFillDarkMode} />
          )}
        </span>
      )}
      {portalTarget && tooltipPopup
        ? createPortal(tooltipPopup, portalTarget)
        : null}
    </span>
  );
};

export default Tooltip;
